<script lang="ts" setup>
import Button from '@/components/Button.vue'
import Modal from '@/components/Modal.vue'
import Message from '@/components/Message.vue'
import type { Product } from '@/lib/types/Product'
import * as CartService from '../services/cart'
import { computed, onMounted, ref } from 'vue'
import formatMoney from '@/lib/formatters/money'
import IconSubscription from '@/icons/subscription.svg?component'
import { track } from '@/services/analytics'

const props = defineProps<{
  product: Product
}>()

const onCheckout = () => {
  CartService.checkout()
}

const onAcceptOffer = async () => {
  track('landingpage_upsell_add', {
    product_id: props.product.id,
  })
  await CartService.add({
    id: props.product.id,
    selectedVariantId: props.product.selectedVariantId.toString(),
    selectedSellingPlanId:
      props.product.variants[
        props.product.selectedVariantId
      ].sellingPlanAllocations[0]?.sellingPlan.id.toString(),
    quantity: 1,
  })
  CartService.checkout()
}

const selectedVariant = computed(
  () => props.product.variants[props.product.selectedVariantId],
)

const selectedSellingPlanId = computed(
  () => selectedVariant.value.sellingPlanAllocations[0].sellingPlan.id,
)

const selectedSellingPlan = computed(() =>
  selectedVariant.value.sellingPlanAllocations.find(
    (spa) => `${spa.sellingPlan.id}` === `${selectedSellingPlanId.value}`,
  ),
)

const originalPrice = computed(() => selectedVariant.value.price)

const price = computed(() => {
  if (selectedSellingPlan.value) {
    return (
      parseInt(selectedVariant.value.price) *
      ((100 -
        selectedSellingPlan.value.sellingPlan.price_adjustments[0].value) /
        100)
    )
  } else {
    return selectedVariant.value.price
  }
})

const productDescriptionEl = ref<HTMLElement | null>(null)

const checkingHeight = ref(true)
const moreContentThanSpace = ref(true)
const showingMoreButton = ref(false)

const showMore = () => {
  if (productDescriptionEl.value) {
    showingMoreButton.value = false
  }
}

const showLess = () => {
  if (productDescriptionEl.value) {
    showingMoreButton.value = true
  }
}

onMounted(() => {
  if (productDescriptionEl.value) {
    const height = productDescriptionEl.value.getBoundingClientRect().height
    const scrollHeight = productDescriptionEl.value.scrollHeight
    if (height < scrollHeight) {
      moreContentThanSpace.value = true
      showingMoreButton.value = true
    } else {
      moreContentThanSpace.value = false
    }
    checkingHeight.value = false
  }
  track('landingpage_upsell_view', {
    product_id: props.product.id,
  })
})
</script>

<template>
  <Modal title="Before you go…" :no-close="true">
    <div class="LandingPageUpsell__Skip">
      <Button @click="onCheckout" :modifiers="['link', 'small']"> Skip </Button>
    </div>
    <div class="LandingPageUpsell">
      <div class="Stack Stack--tiny">
        <p>
          We've picked another product from our range to complement your
          selection:
        </p>

        <div class="LandingPageUpsell__Product">
          <div class="LandingPageUpsell__ProductHead">
            <div
              class="Label Label--dark Label--small"
              v-if="selectedSellingPlan?.sellingPlan.price_adjustments[0].value"
            >
              Save
              {{ selectedSellingPlan?.sellingPlan.price_adjustments[0].value }}%
            </div>
            <img :src="product.image" :alt="product.title" />
            <div>
              <h5>{{ product.title }}</h5>
              <p>
                {{ formatMoney(price) }}
                <s>{{ formatMoney(originalPrice) }}</s>
              </p>
            </div>
          </div>

          <div
            class="LandingPageUpsell__ProductDescription"
            :class="{
              'is-expanded': !showingMoreButton && !checkingHeight,
              'can-expand': showingMoreButton && moreContentThanSpace,
            }"
            v-html="product.description"
            ref="productDescriptionEl"
          ></div>
          <Button
            v-if="moreContentThanSpace && showingMoreButton"
            :modifiers="['link', 'block']"
            @click="showMore"
          >
            Read more
          </Button>
          <Button
            v-if="moreContentThanSpace && !showingMoreButton"
            :modifiers="['link', 'block']"
            @click="showLess"
          >
            Show less
          </Button>
        </div>

        <Message mode="success">
          <template #icon><IconSubscription /></template>
          <template #content>
            <p>
              The initial frequency of this product is set to
              <strong>{{
                selectedSellingPlan?.sellingPlan.name
                  .toLowerCase()
                  .replace('delivery', '')
              }}</strong
              >. You can change or cancel this at any time, no lock-in.
            </p>
          </template>
        </Message>
        <Button :modifiers="['block']" @click="onAcceptOffer">
          Add subscription for {{ formatMoney(price) }}
        </Button>
        <Button :modifiers="['block', 'link']" @click="onCheckout">
          No thanks, go to checkout
        </Button>
      </div>
    </div>
  </Modal>
</template>

<style>
.LandingPageUpsell__Product {
  background-color: var(--color-lichen-25);
  border-radius: var(--radius-large);
  padding: calc(var(--grid-gap) / 2);
  position: relative;
}

.LandingPageUpsell__ProductHead {
  margin-bottom: calc(var(--grid-gap) / 2);

  & > .Label {
    position: absolute;
    top: calc((var(--grid-gap) / 2) + 5px);
    left: calc((var(--grid-gap) / 2) + 5px);
  }

  & > img {
    width: 100%;
    border-radius: var(--radius-small);
    margin-bottom: calc(var(--grid-gap) / 2);
  }

  & > div {
    flex-grow: 1;
  }

  & p {
    margin: 0;

    &:first-child h5 {
      margin: 0 0 5px;
    }
  }
}

.LandingPageUpsell__ProductDescription {
  max-height: 300px;
  overflow: hidden;

  &.is-expanded {
    max-height: none;
  }

  &.can-expand {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 50px;
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0%),
        var(--color-lichen-25)
      );
    }
  }
}

.LandingPageUpsell__Skip {
  position: absolute;
  top: 0;
  right: 10px;
}
</style>
